import { useState, type ReactElement } from "react";
import { Pencil, X } from "lucide-react";
import { Button } from "../../atoms/Button/Button.js";
import { ToggleGroup } from "../../molecules/ToggleGroup/index.js";
import { Tooltip } from "../../molecules/Tooltip/Tooltip.js";
import type { WidgetBodyProps } from "../types.js";
import type { MoodCheckView } from "./types.js";

interface MoodOption {
  readonly value: number;
  readonly emoji: string;
  readonly label: string;
}

const OPTIONS: readonly MoodOption[] = [
  { value: 1, emoji: "😞", label: "Rough" },
  { value: 2, emoji: "😕", label: "Off" },
  { value: 3, emoji: "😐", label: "Steady" },
  { value: 4, emoji: "🙂", label: "Good" },
  { value: 5, emoji: "😄", label: "Great" },
];

/**
 * The privacy line, and it must never overstate what is true.
 *
 * "Not shared" is a claim about where the entry can go, and it stops being
 * accurate the moment the user grants Player AI read access — so the copy
 * follows the PERMISSION rather than restating a constant.
 */
function privacyLine(aiAccessEnabled: boolean): string {
  return aiAccessEnabled
    ? "Private · Used by Player AI"
    : "Just for you. Not shared.";
}

/**
 * Mood Check — Personal dashboard widget body (data-blind, controlled). The
 * persisted check-in arrives as `data.logged` and the dismissal as
 * `data.hidden` (both host-resolved from the mood dataset); Log / Update /
 * Hide / Bring it back all emit `onChange(next)` and the host owns the Player
 * AI-owned writes + query invalidation (plan §14). Bare content; the
 * `DashboardWidget` adapter supplies the `WidgetCard` frame.
 *
 * **Dismissal is SERVER state, not presentation state.** It persists for the
 * actor's current local day, across reloads and devices, and it is a different
 * fact from the check-in: hiding never creates, changes or deletes an entry,
 * and restoring never does either. The widget therefore renders `data.hidden`
 * rather than any local flag — a local flag is exactly what made "hide for
 * today" last until the next refresh.
 *
 * `readOnly` is what the adapter passes when the host supplied no `onChange`
 * — there is no write path, so every control is disabled rather than offering
 * an interaction the server would reject. That now INCLUDES dismissal, because
 * it, too, is a write.
 */
export function MoodCheckWidget(
  props: WidgetBodyProps<MoodCheckView>,
): ReactElement {
  const readOnly = props.readOnly ?? false;
  const logged = props.data?.logged ?? null;
  const hidden = props.data?.hidden ?? false;
  const aiAccessEnabled = props.data?.aiAccessEnabled ?? false;
  const loggedOption =
    logged === null
      ? null
      : (OPTIONS.find((o) => o.value === logged.score) ?? null);
  const [pending, setPending] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);

  if (hidden) {
    return (
      <div className="flex items-center justify-between text-dense-1 text-pb-text-muted">
        <span>Mood check hidden for today.</span>
        {/* eslint-disable-next-line ds/no-hand-built -- kept: an inline text action that takes its row's type and ink; Button link is underlined at inline size, and no approved look row lists the change. */}
        <button
          type="button"
          disabled={readOnly}
          /* The entry rides along untouched: restoring visibility must not
             look like a mood edit to the host's command channel. */
          onClick={() => props.onChange({ ...props.data, hidden: false })}
          className="text-pb-text hover:text-pb-text-secondary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Bring it back
        </button>
      </div>
    );
  }

  const showLogged = loggedOption !== null && !editing;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold">
          Mood check
        </span>
        <span className="flex items-center gap-1.5">
          {showLogged && !readOnly ? (
            // eslint-disable-next-line ds/no-hand-built -- kept: an inline text action that takes its row's type and ink; Button link is underlined at inline size, and no approved look row lists the change.
            <button
              type="button"
              onClick={() => {
                setPending(loggedOption.value);
                setNote(logged?.note ?? "");
                setEditing(true);
              }}
              aria-label="Update mood"
              className="text-dense-1 text-pb-text-muted hover:text-pb-text inline-flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> update
            </button>
          ) : null}
          {/* The hint repeats the button's name, so it describes nothing
              more. */}
          <Tooltip content="Hide for today" describeChild={false}>
            {/* eslint-disable-next-line ds/no-hand-built -- kept: an 18px icon button in muted ink with a 4px corner; Button xs is 24px, and no approved look row lists the change. */}
            <button
              type="button"
              disabled={readOnly}
              onClick={() => props.onChange({ ...props.data, hidden: true })}
              aria-label="Hide mood check for today"
              className="text-pb-text-muted hover:text-pb-text p-0.5 rounded disabled:cursor-not-allowed disabled:opacity-40"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </span>
      </div>

      {showLogged ? (
        <div className="flex flex-col">
          <span className="flex items-center gap-3">
            <span className="text-2xl leading-none">{loggedOption.emoji}</span>
            <span className="min-w-0">
              <span className="block text-sm text-pb-text">
                Logged · {loggedOption.label}
              </span>
              {logged?.note ? (
                <span className="block text-dense-1 text-pb-text-muted truncate">
                  &quot;{logged.note}&quot;
                </span>
              ) : null}
            </span>
          </span>
          <span className="block text-dense-2 text-pb-text-muted mt-2">
            {privacyLine(aiAccessEnabled)}
          </span>
        </div>
      ) : (
        <>
          <span className="block text-sm text-pb-text mb-2">
            How&apos;s your day starting?
          </span>
          {/* A read-only widget disables every option through the native
              fieldset, which ToggleGroup's Buttons honour (look row OK-11). */}
          <fieldset disabled={readOnly} className="contents">
            <ToggleGroup<number>
              aria-label="Mood"
              value={pending}
              onChange={(value) => {
                if (value !== null) setPending(value);
              }}
              options={OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                iconOnly: true as const,
                icon: <span className="text-xl leading-none">{o.emoji}</span>,
              }))}
              className="flex justify-between mb-3"
            />
          </fieldset>
          {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px note field on the raised fill; Input size="sm" is a 28px card-fill field, and no approved look row lists the change. */}
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={readOnly}
            placeholder="Optional note…"
            maxLength={80}
            aria-label="Optional mood note"
            className="w-full bg-pb-card-elevated border border-pb-control-border rounded-md px-2 py-1.5 text-xs text-pb-text placeholder:text-pb-text-muted focus-frame disabled:cursor-not-allowed disabled:opacity-40 mb-2"
          />
          <div className="flex items-center justify-between">
            <span className="text-dense-2 text-pb-text-muted">
              {privacyLine(aiAccessEnabled)}
            </span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const opt = OPTIONS.find((o) => o.value === pending);
                if (opt) {
                  props.onChange({
                    ...props.data,
                    logged: {
                      score: opt.value,
                      note: note.trim() === "" ? null : note.trim(),
                    },
                  });
                  setEditing(false);
                }
              }}
              disabled={pending === null || readOnly}
            >
              {editing ? "Update" : "Log"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
